/**
 * API Connections — Direct REST/GraphQL connections
 *
 * Point-and-click API connections without MCP wrapping.
 * Upload OpenAPI spec + credentials → endpoints become callable tools.
 *
 * Features:
 * - Search across name, base URL, description
 * - Auth type filter
 * - Card grid with endpoint count, latency, uptime
 * - Sheet drill-in for endpoint list with enable/disable toggles
 * - Test, configure, remove actions
 */

import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Globe, MoreHorizontal, RefreshCw, Eye,
  Settings2, Trash2,
  FileJson, Code2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { StatusDot } from '@/components/shared/status-dot';
import { statusToDot, STATUS_BADGE_STYLES } from '@/components/connections/connection-status-badge';
import { AUTH_BADGE_STYLES } from '@/components/connections/auth-type-badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
// Codex retest #266 — `ApiEndpoint` type import was unused in this
// file (the sheet renders endpoint rows inline against the connection
// VM, not against a separate ApiEndpoint binding).
import {
  AUTH_LABELS,
  METHOD_COLORS,
  type ApiConnection,
  type AuthType,
} from '@/lib/mock-connections-data';
import {
  useApiConnections,
  useDeleteApiConnection,
  useTestApiConnection,
} from '@/lib/use-connections-module';
import { useAuth } from '@/contexts/auth-context';
import { useHasPermission } from '@/lib/use-has-permission';
import { decideConnectionsRoleGate } from './-connections-role-gate.helpers';

export const Route = createFileRoute('/connections/api')({
  component: ApiConnectionsPage,
});

// Status helpers and badge styles imported from @/components/connections/

const SPEC_FORMAT_LABELS: Record<string, { label: string; icon: typeof FileJson }> = {
  openapi3: { label: 'OpenAPI 3.0', icon: FileJson },
  openapi2: { label: 'Swagger 2.0', icon: FileJson },
  graphql: { label: 'GraphQL', icon: Code2 },
};

// ── Main Page ──────────────────────────────────────────────────────────────

function ApiConnectionsPage() {
  const navigate = useNavigate();
  // Codex retest #228 round 5 — TanStack treats api.new.tsx +
  // api.$connectionId.tsx as children of this route, so navigating
  // there used to match the child but only this list component
  // painted (no <Outlet />). Hand off to the child route when the
  // path isn't exactly the list. Mirrors automate/playbooks.tsx
  // (#169) — every hook below MUST run on every render so the hook
  // count stays stable across the list ↔ detail transition.
  const location = useLocation();
  const [search, setSearch] = useState('');
  // Real-data: tenant_api_connections
  const { data: realConnections = [] } = useApiConnections();
  const connections = realConnections;
  const deleteConnection = useDeleteApiConnection();
  // Bug #758 / B1-T3: hide the CTA for tenants without write:connections.
  const { contextKind } = useAuth();
  const canWriteConnections = useHasPermission('write', 'connections');
  const canAddApiConnection =
    decideConnectionsRoleGate({ contextKind, canWriteConnections }) !==
    'deny_role_too_low';
  const [authFilter, setAuthFilter] = useState<'all' | AuthType>('all');
  const [sheetConnection, setSheetConnection] = useState<ApiConnection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiConnection | null>(null);

  // Filter
  const filtered = connections.filter(c => {
    if (authFilter !== 'all' && c.authType !== authFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.displayName.toLowerCase().includes(q) ||
      c.baseUrl.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q);
  });

  // Actions — real backend.
  const testConnection = useTestApiConnection();
  const handleTest = (id: string) => {
    const name = connections.find(c => c.id === id)?.displayName ?? 'connection';
    const t = toast.loading(`Testing ${name}...`);
    testConnection.mutate(id, {
      onSuccess: (res) => {
        toast.dismiss(t);
        if (res.ok) {
          toast.success(`${name}: ${res.message}`);
        } else {
          toast.error(`${name}: ${res.message}`);
        }
      },
      onError: (err) => {
        toast.dismiss(t);
        toast.error(`${name}: ${err.message}`);
      },
    });
  };

  // Per-endpoint enable/disable on the list-view sheet calls back into the
  // detail page hook. The list view's quick-sheet is read-only here — the
  // canonical toggle lives on /connections/$id (Endpoints tab) and persists.
  const handleToggleEndpoint = (connectionId: string, _endpointId: string) => {
    const name = connections.find(c => c.id === connectionId)?.displayName;
    toast.info(
      `Open ${name} → Endpoints tab to toggle endpoints (changes there persist).`,
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const name = deleteTarget.displayName;
    const id = deleteTarget.id;
    deleteConnection.mutate(id, {
      onSuccess: () => {
        toast.success(`Removed ${name}`);
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(`Failed to remove ${name}: ${err.message}`);
      },
    });
  };

  // Now that all hooks have run, hand off to the child detail/wizard
  // route when the path isn't the bare list URL. See the comment on
  // `useLocation` above for why this lives at the end of the function.
  const isIndex =
    location.pathname === '/connections/api' ||
    location.pathname === '/connections/api/';
  if (!isIndex) return <Outlet />;

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search connections..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={authFilter} onValueChange={v => setAuthFilter(v as 'all' | AuthType)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Auth Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Auth Types</SelectItem>
            <SelectItem value="api_key">API Key</SelectItem>
            <SelectItem value="oauth2">OAuth 2.0</SelectItem>
            <SelectItem value="basic">Basic Auth</SelectItem>
            <SelectItem value="bearer">Bearer Token</SelectItem>
            <SelectItem value="custom_headers">Custom Headers</SelectItem>
            <SelectItem value="none">No Auth</SelectItem>
          </SelectContent>
        </Select>
        {canAddApiConnection && (
          <Button size="sm" className="gap-1.5" onClick={() => navigate({ to: '/connections/new-api' })}>
            <Globe className="h-3.5 w-3.5" /> Add Connection
          </Button>
        )}
      </div>

      {/* Card grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(conn => {
            const specInfo = SPEC_FORMAT_LABELS[conn.specFormat];
            const SpecIcon = specInfo?.icon ?? FileJson;
            const enabledCount = conn.endpoints.filter(ep => ep.enabled).length;

            return (
              <Card
                key={conn.id}
                className="hover:border-primary/30 transition-colors group"
              >
                <CardContent className="pt-4 pb-3 space-y-3">
                  {/* Header: status + name + auth */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusDot status={statusToDot(conn.status)} size="sm" />
                      <span className="font-medium text-sm truncate">{conn.displayName}</span>
                    </div>
                    <Badge variant="outline" className={cn('text-[9px] shrink-0', AUTH_BADGE_STYLES[conn.authType])}>
                      {AUTH_LABELS[conn.authType]}
                    </Badge>
                  </div>

                  {/* Base URL + spec format */}
                  <div className="space-y-0.5">
                    <p className="text-xs font-mono text-muted-foreground truncate">{conn.baseUrl}</p>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="secondary" className="text-[9px]">
                        <SpecIcon className="h-2.5 w-2.5 mr-0.5" />
                        {specInfo?.label ?? conn.specFormat}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {enabledCount}/{conn.endpoints.length} endpoints active
                      </span>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-sm font-semibold">{enabledCount}</p>
                      <p className="text-[10px] text-muted-foreground">Endpoints</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{conn.metrics.latencyMs != null ? <>{conn.metrics.latencyMs}<span className="text-[10px] font-normal text-muted-foreground">ms</span></> : <span className="text-muted-foreground">—</span>}</p>
                      <p className="text-[10px] text-muted-foreground">Latency</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{conn.metrics.uptimePercent != null ? `${conn.metrics.uptimePercent}%` : <span className="text-muted-foreground">—</span>}</p>
                      <p className="text-[10px] text-muted-foreground">Uptime</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Bottom: status badge + actions */}
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={cn('text-[9px]', STATUS_BADGE_STYLES[conn.status])}>
                      {conn.status}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleTest(conn.id)}
                        aria-label={`Test connection '${conn.displayName}'`}
                        title="Test connection"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setSheetConnection(conn)}
                        aria-label={`View connection '${conn.displayName}'`}
                        title="View"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Connection actions for '${conn.displayName}'`}
                            title="More actions"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate({ to: '/connections/$connectionId', params: { connectionId: conn.id } })}>
                            <Settings2 className="h-3.5 w-3.5 mr-2" /> Configure
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTest(conn.id)}>
                            <RefreshCw className="h-3.5 w-3.5 mr-2" /> Test Connection
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(conn); }}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : search || authFilter !== 'all' ? (
        <div className="text-center py-12">
          <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No connections matching your filters</p>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Globe className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No API connections</p>
          <p className="text-xs text-muted-foreground/70 mt-1 mb-3 max-w-md">
            Connect your REST or GraphQL APIs by uploading an OpenAPI specification.
            Endpoints are automatically discovered and exposed as callable tools.
          </p>
          {canAddApiConnection && (
            <Button variant="outline" size="sm" onClick={() => navigate({ to: '/connections/new-api' })}>
              <Globe className="h-3.5 w-3.5 mr-1.5" /> Add API Connection
            </Button>
          )}
        </div>
      )}

      {/* Endpoint Sheet */}
      <Sheet open={!!sheetConnection} onOpenChange={open => { if (!open) setSheetConnection(null); }}>
        <SheetContent className="w-[480px] sm:max-w-[480px]">
          {sheetConnection && (
            <div className="space-y-4">
              <SheetHeader>
                <div className="flex items-center gap-2">
                  <StatusDot status={statusToDot(sheetConnection.status)} size="sm" />
                  <SheetTitle>{sheetConnection.displayName}</SheetTitle>
                </div>
                {/* Codex retest #243 — visible auth+URL caption serves
                    double duty as the Sheet's accessible description so
                    Radix doesn't warn about missing aria-describedby. */}
                <SheetDescription asChild>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={cn('text-[9px]', AUTH_BADGE_STYLES[sheetConnection.authType])}>
                      {AUTH_LABELS[sheetConnection.authType]}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">{sheetConnection.baseUrl}</span>
                  </div>
                </SheetDescription>
              </SheetHeader>

              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <p className="text-lg font-bold">{sheetConnection.endpoints.filter(ep => ep.enabled).length}</p>
                    <p className="text-[10px] text-muted-foreground">Active</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <p className="text-lg font-bold">{sheetConnection.endpoints.length}</p>
                    <p className="text-[10px] text-muted-foreground">Total</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <Badge variant="outline" className={cn('text-[9px]', STATUS_BADGE_STYLES[sheetConnection.status])}>
                      {sheetConnection.status}
                    </Badge>
                    <p className="text-[10px] text-muted-foreground mt-1">Status</p>
                  </CardContent>
                </Card>
              </div>

              <Separator />

              {/* Endpoint list */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Endpoints</h4>
                <div className="space-y-2">
                  {sheetConnection.endpoints.map(ep => (
                    <div
                      key={ep.id}
                      className={cn(
                        'p-3 rounded-lg transition-colors',
                        ep.enabled ? 'bg-muted/30 hover:bg-muted/50' : 'bg-muted/10 opacity-60'
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className={cn('text-[9px] font-mono shrink-0', METHOD_COLORS[ep.method])}>
                            {ep.method}
                          </Badge>
                          <span className="text-xs font-mono truncate">{ep.path}</span>
                        </div>
                        <Switch
                          checked={ep.enabled}
                          onCheckedChange={() => handleToggleEndpoint(sheetConnection.id, ep.id)}
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground">{ep.summary}</p>
                      {ep.enabled && ep.callCount > 0 && (
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground/70">
                          <span>{ep.callCount.toLocaleString()} calls</span>
                          <span>~{ep.avgLatencyMs}ms avg</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove API Connection</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.displayName}</strong>?
              This will disable all {deleteTarget?.endpoints.filter(ep => ep.enabled).length} active endpoints.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Remove Connection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
